import {chunk, isEmpty, startsWith, uniq} from 'lodash';
import {HoistService, InitContext, XH} from '@xh/hoist/core';
import {action, observable} from '@xh/hoist/mobx';
import {getDefaultOptions} from '../../core/ServiceUtils';
import {IntradaySeries, Position, WatchListUpdate} from '../data/DataTypes';
import {NotificationMessage} from '../data/MessageTypes';

/** Points in a full session: the prior close, then 78 five-minute bars from 09:30 to 16:00. Matches the backend's ExchangeConfig. */
export const SESSION_POINTS = 79;

/** Most tickers one request takes, as IntradayController allows. */
const MAX_TICKERS = 300;

/** A missing or out-of-date series is loaded again at most this often. */
const RELOAD_AFTER_MS = 60 * 1000;

/**
 * Today's 5-minute series of tickers and portfolio balances, from the Intraday API.
 *
 * After the first load of a ticker's series, its latest bars come with the position and watchlist updates, and are
 * written into the series here. The position updates are read here. A watchlist passes its own updates to
 * `applyWatchlistBars`, so the watchlist topic stays subscribed only while a watchlist is shown.
 *
 * A portfolio's balance series is recorded by the backend and has no live bar, so the portfolio panel polls it with
 * `loadPortfolioAsync`.
 *
 * Each series is replaced rather than changed, in a shallow map, so a chart that reads one series re-renders only when
 * that one gets a new bar. See docs/decisions/0051 in rzx-backend.
 */
export class IntradayService extends HoistService {
    private series = observable.map<string, number[]>(new Map(), {deep: false});
    /** When each series was last requested - set before the load, so a series is never requested twice at once. */
    private loadedAt = new Map<string, number>();

    override async initAsync(ctx: InitContext): Promise<void> {
        XH.messageHub.subscribe('portfolio', this.xhId, (message: NotificationMessage) => this.onPortfolioMessage(message));

        // Every ticker held, for the positions grids. Runs only when the set of tickers changes.
        this.addReaction({
            track: () =>
                uniq(XH.livePositionsService.cube.store.allRecords.map(r => r.data.ticker as string))
                    .sort()
                    .join(','),
            run: tickers => this.loadTickersAsync(tickers.split(',')),
            fireImmediately: true
        });
    }

    /** Today's prices of a ticker, starting at the prior close. Null until loaded, and for indices, futures and cash. */
    tickerPoints(ticker: string): number[] {
        return this.series.get(ticker) ?? null;
    }

    /** Today's balance of a portfolio, starting at the prior close. Null until loaded. */
    portfolioPoints(portfolioId: number): number[] {
        return this.series.get(portfolioKey(portfolioId)) ?? null;
    }

    /** Loads the tickers' series not loaded yet - or all of them, with `force`. */
    async loadTickersAsync(tickers: string[], force: boolean = false): Promise<void> {
        const keys = uniq(tickers).filter(t => hasSeries(t) && (force || !this.loadedAt.has(t)));
        if (isEmpty(keys)) return;

        this.markRequested(keys);
        await Promise.all(
            chunk(keys, MAX_TICKERS).map(async batch => {
                try {
                    const series: IntradaySeries[] = await XH.fetchService.getJson({
                        url: `${XH.getConf('svcApiUrl')}/intraday/tickers`,
                        params: {tickers: batch.join(',')},
                        ...getDefaultOptions()
                    });
                    this.setSeries(series.map(s => [s.ticker, s.points]));
                } catch (e) {
                    // A ticker left without a series is loaded again by its next live bar
                    console.error(`Unable to load intraday series for ${batch.length} tickers`, e);
                }
            })
        );
    }

    /** Loads a portfolio's balance series if not loaded yet - or again, with `force`. */
    async loadPortfolioAsync(portfolioId: number, force: boolean = false): Promise<void> {
        const key = portfolioKey(portfolioId);
        if (portfolioId == null || (!force && this.loadedAt.has(key))) return;

        this.markRequested([key]);
        try {
            const series: IntradaySeries = await XH.fetchService.getJson({
                url: `${XH.getConf('svcApiUrl')}/intraday/portfolio/${portfolioId}`,
                ...getDefaultOptions()
            });
            this.setSeries([[key, series.points]]);
        } catch (e) {
            console.error(`Unable to load intraday series for portfolio ${portfolioId}`, e);
        }
    }

    /** Writes the latest bar of each watchlist ticker into its series. */
    applyWatchlistBars(updates: WatchListUpdate[]) {
        this.applyBars((updates ?? []).map(u => [u.ticker, u.intradayIndex, u.price]));
    }

    //------------------------
    // Implementation
    //------------------------
    private onPortfolioMessage(message: NotificationMessage) {
        const {eventType} = message.envelope;
        if (eventType !== 'ADD' && eventType !== 'UPDATE') return;

        const positions = (message.payload?.positions ?? []) as Position[];
        this.applyBars(positions.map(p => [p.ticker, p.intradayIndex, p.price]));
    }

    /**
     * Sets point `index` of each ticker's series to the price, carrying the last point forward over any gap. A series
     * that is missing (its load failed) or ends past the bar (it is from the previous session) is loaded again instead.
     * Bars outside the session have no index and are skipped, as are tickers no one has asked for.
     */
    @action
    private applyBars(bars: Bar[]) {
        const reload: string[] = [];
        bars.forEach(([key, index, price]) => {
            if (index == null || price == null || !this.loadedAt.has(key)) return;

            const points = this.series.get(key);
            if (!points || index < points.length - 1) {
                if (Date.now() - this.loadedAt.get(key) >= RELOAD_AFTER_MS) reload.push(key);
                return;
            }

            const next = points.slice();
            for (let i = points.length; i < index; i++) next[i] = next[i - 1];
            next[index] = price;
            this.series.set(key, next);
        });

        this.loadTickersAsync(reload, true);
    }

    @action
    private setSeries(entries: [string, number[]][]) {
        entries.forEach(([key, points]) => (isEmpty(points) ? this.series.delete(key) : this.series.set(key, points)));
    }

    private markRequested(keys: string[]) {
        const now = Date.now();
        keys.forEach(key => this.loadedAt.set(key, now));
    }
}

/** Ticker, the index of the bar, and the price. */
type Bar = [string, number, number];

// Portfolio series share the map with ticker series, under a prefix no ticker has
const PORTFOLIO_PREFIX = '#portfolio:';

function portfolioKey(portfolioId: number): string {
    return portfolioId == null ? null : `${PORTFOLIO_PREFIX}${portfolioId}`;
}

/** Cash ($), indices (^) and futures (/) are priced from other sources and have no series. */
function hasSeries(ticker: string): boolean {
    return !!ticker && !startsWith(ticker, '$') && !startsWith(ticker, '^') && !startsWith(ticker, '/');
}
